import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRepMode } from "./RepModeContext";
import { fetchRepAthletes } from "./repServices";
import { ARCHETYPE_META, EMPTY_VALUE, PLATFORM_LABELS, filterPlayers, fmtCompact, fmtMoney, fmtMoneyCompact, initialsOf, rosterCounts } from "../../ForCoaches/v2/coachUtils";
import {
  BrandCategoryGrid,
  EngagementByPlatform,
  HighlightTile,
  NilBreakdownTable,
  PillarsTable,
  PositionValueChart,
  RosterTable,
  StatCard,
  StatusLegend,
  SwingComposition,
} from "../../ForCoaches/v2/coachPieces";
import { Badge, Btn, Card, Chip, ErrorState, Loading, MetricCard, Overline, PageHeader } from "../../ForBrands/ui/BrandUI";
import BrandLogo from "../../common/BrandLogo";
import { fmtIndex10 } from "../../../utils/indexScore";

// Rep Mode · Athletes (Figma 02 · Roster, shared with Coach Mode): the
// athletes who linked this rep, with the same Roster / Performance / NIL
// Intelligence views a coach gets for a team. Scores and valuations are
// market data; deal amounts are never shown here. Performance, Visibility
// and Off-court read out of 10 (utils/indexScore), the CacheScore out of 100.

const SUB_TABS = [
  { value: "roster", label: "Roster" },
  { value: "performance", label: "Performance" },
  { value: "nil", label: "NIL Intelligence" },
];

const RepAthletesPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { openShare } = useRepMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(params.get("filter") || "all");
  const tab = SUB_TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "roster";

  const load = async (silent = false) => {
    if (!silent) setData(null);
    try {
      const payload = await fetchRepAthletes();
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your athletes");
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!data?.nil?.matchesComputing) return undefined;
    const t = setTimeout(() => load(true), 30000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const players = useMemo(() => data?.players || [], [data]);
  const counts = useMemo(() => rosterCounts(players), [players]);
  const filtered = useMemo(() => filterPlayers(players, filter), [players, filter]);
  const active = useMemo(() => players.filter((p) => p.status === "active"), [players]);
  const sharing = useMemo(() => players.filter((p) => p.shareFinancials).length, [players]);

  const setTab = (value) => setParams({ tab: value, ...(filter !== "all" ? { filter } : {}) });

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { team, summary, performance, nil } = data;
  const totals = team?.totals || {};
  const h = performance?.highlights || {};
  const nh = nil?.highlights || {};
  const snap = performance?.teamSnapshot || {};

  return (
    <div>
      <PageHeader
        eyebrow="Rep Mode"
        title="Your athletes"
        subtitle={`${totals.total ?? 0} athlete${totals.total === 1 ? "" : "s"} linked to your Rep ID · ${totals.activated ?? 0} activated · ${sharing} sharing financial terms`}
        actions={<Btn onClick={openShare}>Share your Rep ID</Btn>}
      />

      <div className="flex flex-col gap-4 pt-2">
        <div className="flex flex-wrap gap-3">
          <MetricCard muted label="Total CacheValue" value={fmtMoneyCompact(summary.cacheValueTotal)} sub={`${totals.activated ?? 0} activated profiles`} className="!flex-none min-w-[230px]" />
          <MetricCard label="Avg. CacheScore" value={summary.avgScore ?? EMPTY_VALUE} sub={summary.avgScoreDelta != null ? `${summary.avgScoreDelta >= 0 ? "+" : ""}${summary.avgScoreDelta} in 30 days` : "no trend yet"} />
          <MetricCard label="Total NILx value" value={fmtMoneyCompact(summary.nilxPool)} sub="across active profiles" />
          <MetricCard label="NIL readiness" value={`${summary.readinessLinked ?? 0} of ${totals.total ?? 0}`} sub="social accounts linked" />
        </div>

        <div className="flex items-center gap-2 border-b border-bm-border">
          {SUB_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              className={`-mb-px border-b-2 px-4 pb-3 pt-2 font-archivo text-[15px] font-bold leading-5 ${tab === t.value ? "border-bm-orange text-bm-orange" : "border-transparent text-bm-muted hover:text-bm-ink"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "roster" && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {[
                ["all", "All athletes"],
                ["activated", "Activated"],
                ["pending", "Pending"],
                ["standouts", "Standouts"],
              ].map(([key, label]) => (
                <Chip key={key} active={filter === key} count={counts[key]} onClick={() => setFilter(key)}>
                  {label}
                </Chip>
              ))}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Top mover" value={performance?.topMover?.name || EMPTY_VALUE} badge={performance?.topMover ? `${performance.topMover.delta >= 0 ? "+" : ""}${performance.topMover.delta} pts CacheScore` : "no trend yet"} badgeTone={performance?.topMover ? "green" : "muted"} />
              <StatCard label="Highest CacheScore" value={h.highest ? `${h.highest.name} ${h.highest.surname}` : EMPTY_VALUE} badge={h.highest ? `${h.highest.score} CacheScore` : null} />
              <StatCard label="NIL readiness" value={`${summary.readinessLinked ?? 0} of ${totals.activated ?? 0} active`} badge={totals.activated ? `${Math.round((100 * (summary.readinessLinked || 0)) / totals.activated)}% linked` : null} />
              <StatCard label="Profiles complete" value={`${summary.profilesComplete ?? 0} of ${totals.total ?? 0}`} badge={`${Math.max(0, (totals.total ?? 0) - (summary.profilesComplete ?? 0))} need attention`} badgeTone="muted" />
            </div>
            <StatusLegend />
            <RosterTable players={filtered} onOpen={(p) => navigate(`/business/athlette/profile/${p.athleteId}`)} emptyText="Athletes who add your Rep ID in My Circle appear here." />
          </>
        )}

        {tab === "performance" && (
          <>
            <Card className="flex flex-col gap-4">
              <h2 className="bm-h-m text-bm-ink">Performance Snapshot</h2>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {[
                  { value: summary.avgScore ?? EMPTY_VALUE, label: "Avg. CacheScore™", chip: summary.nationalAvgScore != null && summary.avgScore != null ? `${summary.avgScore >= summary.nationalAvgScore ? "Above" : "Below"} national avg ${summary.nationalAvgScore}` : null },
                  { value: fmtMoneyCompact(summary.cacheValueTotal), label: "Total CacheValue™", chip: `${totals.activated ?? 0} activated` },
                  { value: fmtMoneyCompact(snap.cacheValuePerPlayer), label: "CacheValue™ per athlete", chip: "active athletes" },
                  { value: h.depth ? `Tier 1 x${h.depth.tier1} · Tier 2 x${h.depth.tier2}` : EMPTY_VALUE, label: "Depth", chip: "CacheScore™ 80+ / 70+" },
                ].map((cell) => (
                  <div key={cell.label} className="flex flex-col items-center gap-2 rounded-[8px] border border-bm-border p-5 text-center">
                    <span className="bm-num-l text-bm-ink">{cell.value}</span>
                    <Overline>{cell.label}</Overline>
                    {cell.chip && <Badge tone="passive">{cell.chip}</Badge>}
                  </div>
                ))}
              </div>
            </Card>
            <SwingComposition players={players} />
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
              <PositionValueChart team={performance?.positionValues} population={performance?.positionPopulation} />
              <PillarsTable players={active} />
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {h.highest && <HighlightTile eyebrow="Highest CacheScore™" avatar={h.highest.avatar} initials={initialsOf(h.highest)} title={`${h.highest.name} ${h.highest.surname} · ${h.highest.score}`} chip={h.highest.trend != null ? `${h.highest.trend >= 0 ? "+" : ""}${h.highest.trend} pts in 30d` : "no trend yet"} chipDot={h.highest.trend >= 0 ? "#1f9d55" : "#e2241a"} />}
              {h.biggestSwing && <HighlightTile eyebrow="Biggest swing" avatar={h.biggestSwing.avatar} initials={initialsOf(h.biggestSwing)} title={`${h.biggestSwing.name} ${h.biggestSwing.surname}`} chip={ARCHETYPE_META[h.biggestSwing.archetype]?.label} chipDot={ARCHETYPE_META[h.biggestSwing.archetype]?.color} />}
              {h.offCourtLeader && <HighlightTile eyebrow="Off-court leader" avatar={h.offCourtLeader.avatar} initials={initialsOf(h.offCourtLeader)} title={`${h.offCourtLeader.name} ${h.offCourtLeader.surname} · ${fmtIndex10(h.offCourtLeader.offCourt, { suffix: true })}`} chip="Highest off-court impact" chipDot="#1f9d55" />}
              {h.depth && <HighlightTile eyebrow="Depth" logo={<span className="font-archivo text-[22px] font-bold text-bm-ink">{(h.depth.tier1 || 0) + (h.depth.tier2 || 0)}</span>} title={`Tier 1 x${h.depth.tier1} · Tier 2 x${h.depth.tier2}`} chip="CacheScore™ 80+ / 70+" />}
            </div>
          </>
        )}

        {tab === "nil" && (
          <>
            <div className="flex flex-wrap gap-3">
              <MetricCard label="NILx pool potential" value={fmtMoneyCompact(summary.nilxPool)} sub={`${totals.activated ?? 0} active profiles`} />
              <MetricCard label="Total social reach" value={nil?.totalReach != null ? fmtCompact(nil.totalReach) : EMPTY_VALUE} sub="across all platforms" />
              <MetricCard
                label="Avg. engagement rate"
                value={nil?.avgEngagement != null ? `${nil.avgEngagement}%` : EMPTY_VALUE}
                sub={nil?.avgEngagement != null ? `${nil.avgEngagement >= nil.benchmark ? "+" : ""}${(nil.avgEngagement - nil.benchmark).toFixed(1)}pp vs ${nil.benchmark}% benchmark` : `vs ${nil?.benchmark ?? EMPTY_VALUE}% benchmark`}
              />
            </div>
            <SwingComposition players={players} />
            <div className="grid gap-4 lg:grid-cols-2">
              <EngagementByPlatform rows={nil?.platformEngagement} />
              <BrandCategoryGrid categories={nil?.brandCategories} matches={nil?.teamBrandMatches} computing={nil?.matchesComputing} />
            </div>
            <NilBreakdownTable players={active} />
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {nh.highestNilx && <HighlightTile eyebrow="Highest NILx" avatar={nh.highestNilx.avatar} initials={initialsOf(nh.highestNilx)} title={`${nh.highestNilx.name} ${nh.highestNilx.surname} · ${fmtMoney(nh.highestNilx.nilx)}`} chip={ARCHETYPE_META[nh.highestNilx.archetype]?.label} chipDot={ARCHETYPE_META[nh.highestNilx.archetype]?.color} />}
              {nh.biggestOpportunity && <HighlightTile eyebrow="Biggest opportunity" avatar={nh.biggestOpportunity.avatar} initials={initialsOf(nh.biggestOpportunity)} title={`${nh.biggestOpportunity.name} ${nh.biggestOpportunity.surname} · ${fmtMoney(nh.biggestOpportunity.gap)}`} chip="Gap vs. score" chipDot="#edd200" />}
              {nh.platformSaturation && (
                <HighlightTile
                  eyebrow="Platform saturation"
                  logo={<BrandLogo domain={`${nh.platformSaturation.platform === "x" ? "x" : nh.platformSaturation.platform}.com`} mono={PLATFORM_LABELS[nh.platformSaturation.platform]?.slice(0, 2)} size={36} />}
                  title={`${PLATFORM_LABELS[nh.platformSaturation.platform]} ${nh.platformSaturation.pct}%`}
                  chip={`${PLATFORM_LABELS[nh.platformSaturation.growPlatform]} ${nh.platformSaturation.growPct}%, grow`}
                />
              )}
              {nh.brandFitLeader && <HighlightTile eyebrow="Brand fit leader" avatar={nh.brandFitLeader.avatar} initials={initialsOf(nh.brandFitLeader)} title={`${nh.brandFitLeader.name} ${nh.brandFitLeader.surname} · ${fmtIndex10(nh.brandFitLeader.visibility, { suffix: true })}`} chip="Highest brand alignment" chipDot="#1f9d55" />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RepAthletesPage;
